import PersonIcon from "@mui/icons-material/Person";
import { Box, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { useFormatPersonName } from "@/hooks/useNameFormat";
import { type DropdownItem, SystemDropdown } from "@/ui/Inputs/SystemDropDown";
import { SystemValidationTextField } from "@/ui/Inputs/SystemValidationTextField";
import type { Nurse } from "@/utils/models";
import { CharCounter, getSmsMaxChars, getSmsSpecialChars, maxCharsDefault } from "../CharCounter";

const MESSAGE_BOX_ROWS = 6;

export type MessageBoxProps = {
	messageValue: string;
	handleMessageChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
	selectedNurseList: Nurse[];
	nursesOptions: DropdownItem<Nurse>[];
	onDropdownSelectionChange: (selectedValues: Nurse[]) => void;
	isMobile?: boolean;
	lockRecipientSelection?: boolean;
	/** No one can be picked until the staff list loads; a failed load says so under the picker. */
	isRosterLoading: boolean;
	isRosterError: boolean;
	/** Set for an announcement to the building: how many texts it sends (undefined while counting), in place of
	 *  the picked staff. */
	announcement?: {
		recipientCount: number | undefined;
		/** The count failed: say so, instead of counting forever. Sending still works. */
		countFailed: boolean;
		facilityName: string;
	};
};

export const MessageBox: FC<MessageBoxProps> = ({
	messageValue,
	handleMessageChange,
	selectedNurseList,
	nursesOptions,
	onDropdownSelectionChange,
	isMobile,
	lockRecipientSelection,
	isRosterLoading,
	isRosterError,
	announcement,
}) => {
	const { t } = useTranslation();
	const formatPersonName = useFormatPersonName();
	const recipientNames = selectedNurseList.map(formatPersonName).join(", ");
	const specialChars = getSmsSpecialChars(messageValue);

	return (
		<>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					<Box style={{ fontWeight: 600, fontSize: isMobile ? "14px" : "16px" }}>{t("will.be.sent.to")}</Box>
					<Box
						style={{ fontWeight: 600 }}
						sx={{
							display: "flex",
							alignItems: "center",
						}}
						data-testid="broadcast-recipient-count"
					>
						{<PersonIcon style={{ fontSize: "18px" }} />}{" "}
						{announcement
							? (announcement.recipientCount ?? t(announcement.countFailed ? "broadcast.recipients.count.unknown" : "broadcast.recipients.counting"))
							: selectedNurseList.length}
						<InHouseTooltip
							title={announcement ? t("broadcast.facility.recipients.hint", { facility: announcement.facilityName }) : t("broadcast.recipients.need.account")}
						>
							<span style={{ color: "red" }}>*</span>
						</InHouseTooltip>
					</Box>
				</Box>

				{/* An announcement's recipients are picked by the server, so there is no one to pick here. */}
				{!announcement && (
					<SystemDropdown
						label={t("broadcast.select.staff")}
						showFilter
						items={nursesOptions}
						valueToString={formatPersonName}
						selectedItems={selectedNurseList}
						multiple
						disabled={lockRecipientSelection || isRosterLoading || isRosterError}
						// Locked: name the recipients. The default summary shows "All (N)", which with one locked
						// option reads "All (1)" in place of the name the message is addressed to.
						renderValue={lockRecipientSelection ? () => recipientNames : undefined}
						formControlSx={{
							width: isMobile ? "140px" : "250px",
						}}
						onChangeMultiple={onDropdownSelectionChange}
					/>
				)}
			</Box>
			{!announcement && isRosterError && (
				<Typography variant="caption" color="error" data-testid="broadcast-roster-error">
					{t("profile.roster.unavailable")}
				</Typography>
			)}
			{announcement?.countFailed && (
				<Typography variant="caption" sx={{ color: "text.secondary", width: "100%" }} data-testid="broadcast-recipient-count-failed">
					{t("broadcast.recipients.count.failed")}
				</Typography>
			)}
			<Box
				sx={{
					position: "relative",
					width: "100%",
					py: 0.5,
				}}
			>
				<SystemValidationTextField
					variant={isMobile ? "outlined" : "standard"}
					reserveHeightForHelperText={false}
					injectedProps={{
						multiline: true,
						rows: MESSAGE_BOX_ROWS,
						slotProps: {
							htmlInput: {
								maxLength: maxCharsDefault,
								style: {
									scrollbarWidth: "thin",
									scrollbarColor: "#888 transparent",
								},
							},
						},
					}}
					formControlSx={{
						width: "100%",
					}}
					value={messageValue}
					onChange={handleMessageChange}
					placeholder={t("type.your.message")}
				/>

				<CharCounter
					text={messageValue}
					maxChars={getSmsMaxChars(messageValue)}
					sxProps={{
						position: "absolute",
						top: -6,
						right: 0,
					}}
				/>
				{specialChars.length > 0 && (
					<Typography variant="caption" color="text.secondary" data-testid="broadcast-special-chars-hint">
						{t("sms.special.chars.limit", { chars: specialChars.map((char) => `“${char}”`).join(" "), max: getSmsMaxChars(messageValue) })}
					</Typography>
				)}
			</Box>
		</>
	);
};
